/*
 * Render the objectives for a project, also used to show a description for
 * some plain scratchpads.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import createReactClass from "create-react-class";
import PropTypes from "prop-types";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {color} from "@khanacademy/wonder-blocks-tokens";
import Toolbar from "@khanacademy/wonder-blocks-toolbar";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {staticUrl} from "@repo/urls";

import BackboneStateMixin from "#backbone-state-mixin-package/backbone-state-mixin.tsx";
import withBackboneModels from "#react-components-package/with-backbone-models.tsx";
import type {
    Scratchpad,
    UserScratchpad,
} from "#scratchpad-base-package/scratchpads.ts";
import ChallengeCritter from "#scratchpad-challenge-package/challenge-critter.tsx";
import ChallengeProgress from "#scratchpad-challenge-package/challenge-progress.tsx";
import {ScratchpadChallenge} from "#scratchpad-challenge-package/scratchpad-challenge.ts";

import CompletionCritter from "./completion-critter.tsx";
import SaveSpinoffButtons from "./scratchpad-save-spinoff-buttons.tsx";
import type {SaveProgramFn} from "./scratchpad-ui.tsx";
import type {LiveEditor} from "./types.ts";

import arrowsCounterClockwiseIcon from "@phosphor-icons/core/regular/arrows-counter-clockwise.svg";

const {CritterAppearance, getCritterAppearanceReason} = ChallengeCritter;

type ScratchpadToolbarProps = {
    // The challenge we are currently facing
    // @ts-expect-error - TS2749 - 'ScratchpadChallenge' refers to a value, but is being used as a type here. Did you mean 'typeof ScratchpadChallenge'?
    challenge: ScratchpadChallenge | null | undefined;
    // If the page is embedded into another page using an iframe
    embeddedAndWithFrame?: boolean;
    // Determine if the save button should be hidden
    hideSave: boolean;
    // If we are in the edit mode for a scratchpad (as a dev) and have
    // permission to edit this scratchpad
    inEditModeAndCanEdit?: boolean;
    // Is this particular scratchpad a challenge
    isChallenge: boolean;
    // Is this scratchpad new (e.g. on the /new page)
    isNew?: boolean;
    // Is this a project?
    isProject: boolean;
    // Handle when the "Start over" button is clicked
    onStartOver: () => unknown;
    // Handle when the "Request help" button is clicked
    onRequestHelp: () => unknown;
    // Handle when the "Undo" button is clicked
    onUndo: () => unknown;
    // The label to use for the restart button
    restartLabel: string;
    // The scratchpad Backbone model which is being rendered
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    scratchpad: Scratchpad;
    // Should the project help button be shown
    showProjectHelpButton: boolean;
    // Should the start over button be shown
    showStartOverButton: boolean;
    // Topic used for getting the relativeUrl for linking
    topic: {
        relativeUrl: string;
    };
    // The UserScratchpad in use on the project or challenge
    // @ts-expect-error - TS2749 - 'UserScratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof UserScratchpad'?
    userScratchpad: UserScratchpad;
    // Is this scratchpad able to be saved using a normal "Save" button
    isSaveable: boolean;
    // Determine if a spin-off is currently allowed
    isSpinOffAllowed: () => boolean;
    // A place to register a callback for external triggering of
    // the save flow on a scratchpad
    registerTriggerSave: (
        callback: (saveSettings: boolean) => unknown,
    ) => unknown;
    // A place to register a callback for external triggering of
    // the spin-off flow on a scratchpad
    registerTriggerSpinOff: (callback: () => unknown) => unknown;
    // Handle saving the current Program (previously known as a Scratchpad)
    saveProgram: SaveProgramFn;
    // Handle setting the title on the scratchpad
    setTitle: (title: string) => Promise<unknown>;
    // A promsie that'll resolve once the editor is ready
    liveEditorPromise: Promise<LiveEditor>;
    // A way to register a callback that will fire when the code changes
    registerCodeChange: (callback: () => unknown) => unknown;
    // A function that determines whether the current code should
    // cause the restart button to be hidden
    shouldHideRestart: () => boolean;
};

type ScratchpadToolbarState = {
    showStartOverModal: boolean;
    disablePasteOpened: boolean;
};

class ScratchpadToolbar extends React.Component<
    ScratchpadToolbarProps,
    ScratchpadToolbarState
> {
    constructor(props: ScratchpadToolbarProps) {
        super(props);
        this.state = {
            showStartOverModal: false,
            disablePasteOpened: false,
        };
    }

    componentDidMount() {
        this.props.liveEditorPromise.then(this.handleLiveEditorReady);
    }

    handleLiveEditorReady: (liveEditor: LiveEditor) => void = (
        liveEditor: LiveEditor,
    ) => {
        const editor = liveEditor.editor;

        // For a subset of challenges/projects, we only allow copy and paste
        // of code that was originally copied/cut from the editor.
        // That way, they cant paste in code from elsewhere.
        // Currently applies only in Intro to JS, as that is the topic
        // most frequently assigned by teachers, thus most subject to cheating.
        // Also, students sometimes have legitimate reasons to copy/paste in
        // the advanced JS, HTML, and SQL challenges.
        const disableCopyPaste =
            (this.props.isProject || this.props.isChallenge) &&
            this.props.topic.relativeUrl.indexOf("/programming/") > -1;

        if (disableCopyPaste && editor.blockPaste) {
            editor.blockPaste(() => {
                this.setState({disablePasteOpened: true});
            });
        }
    };

    /* Some common buttons */

    renderStartOverButton(): React.ReactNode {
        if (
            this.props.embeddedAndWithFrame ||
            !this.props.showStartOverButton
        ) {
            return null;
        }

        const startOverModal = ({closeModal}: {closeModal: () => void}) => (
            <OnePaneDialog
                style={styles.shortModal}
                title={t`Start over`}
                content={
                    <Body tag="p">
                        {t`Are you sure you want to discard your changes? This can't be undone.`}
                    </Body>
                }
                footer={
                    <View style={styles.modalFooter}>
                        <Button kind="tertiary" onClick={closeModal}>
                            {t`Cancel`}
                        </Button>
                        <Strut size={16} />
                        <Button
                            color="destructive"
                            onClick={() => {
                                closeModal();
                                this.props.onStartOver();
                            }}
                        >
                            {t`Start over`}
                        </Button>
                    </View>
                }
            />
        );

        return (
            <ModalLauncher modal={startOverModal}>
                {({openModal}) => (
                    <Button
                        kind="secondary"
                        style={styles.buttonOnLeft}
                        onClick={openModal}
                    >
                        {t`Start over`}
                    </Button>
                )}
            </ModalLauncher>
        );
    }

    renderUndoButton(): React.ReactNode {
        return (
            this.props.isChallenge && (
                <Button
                    kind="secondary"
                    onClick={this.props.onUndo}
                    style={styles.buttonOnLeft}
                >
                    {t`Undo`}
                </Button>
            )
        );
    }

    // This button is useful both for when programs are embedded inside KA
    // articles and when programs are embedded outside of KA.

    renderShowOnKAButton(): React.ReactNode {
        if (
            this.props.isNew ||
            !this.props.embeddedAndWithFrame ||
            this.props.isChallenge
        ) {
            return null;
        }

        return (
            <Button
                kind="secondary"
                href={this.props.scratchpad.get("url")}
                target="_blank"
                style={styles.buttonOnRight}
            >
                {t`View in new tab`}
            </Button>
        );
    }

    // A few project-specific buttons

    renderProjectHelpButton(): React.ReactNode {
        if (
            this.props.embeddedAndWithFrame ||
            !this.props.showProjectHelpButton
        ) {
            return null;
        }

        // Users must manually create spin-offs from projects.
        const mustSpinOff = this.props.isProject;

        const helpRequestModal = ({closeModal}: {closeModal: () => void}) => (
            <OnePaneDialog
                style={styles.shortModal}
                title={t`Request help`}
                content={
                    <Body tag="p">
                        {t`If you'd like to request help on this project, please make a spin-off of the project first.`}
                    </Body>
                }
                footer={
                    <View style={styles.modalFooter}>
                        <Button
                            onClick={() => {
                                closeModal();
                            }}
                        >
                            {t`Okay`}
                        </Button>
                    </View>
                }
            />
        );

        return (
            <ModalLauncher modal={helpRequestModal}>
                {({openModal}) => (
                    <Button
                        kind="secondary"
                        style={styles.buttonOnLeft}
                        onClick={() => {
                            if (mustSpinOff) {
                                openModal();
                            } else {
                                this.props.onRequestHelp();
                            }
                        }}
                    >
                        {t`Request help`}
                    </Button>
                )}
            </ModalLauncher>
        );
    }

    renderProgress(): React.ReactNode {
        const {challenge} = this.props;

        if (!challenge) {
            return null;
        }

        // @ts-expect-error - TS2769 - No overload matches this call.
        return <ChallengeTools challenge={challenge} />;
    }

    renderSaveButtons(): React.ReactNode {
        const {
            isSaveable,
            isSpinOffAllowed,
            registerTriggerSave,
            registerTriggerSpinOff,
            saveProgram,
            scratchpad,
            setTitle,
        } = this.props;
        return (
            <SaveSpinoffButtons
                isSaveable={isSaveable}
                isSpinOffAllowed={isSpinOffAllowed}
                registerTriggerSave={registerTriggerSave}
                registerTriggerSpinOff={registerTriggerSpinOff}
                saveProgram={saveProgram}
                scratchpad={scratchpad}
                setTitle={setTitle}
            />
        );
    }

    renderPasteDisabled(): React.ReactNode {
        const msg = t`To make sure you get the most out of these challenges, we've disabled pasting code from outside. Studies show that typing code helps our brain solidify the concepts more.`;
        return (
            <ModalLauncher
                onClose={() => {
                    this.setState({disablePasteOpened: false});
                }}
                opened={this.state.disablePasteOpened}
                modal={({closeModal}) => (
                    <OnePaneDialog
                        style={styles.shortModal}
                        title={t`Sorry!`}
                        content={<Body>{msg}</Body>}
                        footer={<Button onClick={closeModal}>{t`Okay`}</Button>}
                    />
                )}
            />
        );
    }

    render(): React.ReactNode {
        const left = (
            <React.Fragment>
                {this.renderUndoButton()}
                {this.renderStartOverButton()}
                {this.renderProjectHelpButton()}
                {this.renderPasteDisabled()}
                <ErrorBuddy />
            </React.Fragment>
        );

        const {
            liveEditorPromise,
            registerCodeChange,
            restartLabel,
            shouldHideRestart,
        } = this.props;

        const right = (
            <React.Fragment>
                <RestartButton
                    liveEditorPromise={liveEditorPromise}
                    registerCodeChange={registerCodeChange}
                    restartLabel={restartLabel}
                    shouldHideRestart={shouldHideRestart}
                />
                {this.renderShowOnKAButton()}
                {this.renderProgress()}
                {this.renderSaveButtons()}
            </React.Fragment>
        );

        return <Toolbar leftContent={left} rightContent={right} />;
    }
}

type RestartButtonProps = {
    // A promise that'll resolve once the editor is ready
    liveEditorPromise: Promise<LiveEditor>;
    // A way to register a callback that will fire when the code changes
    registerCodeChange: (callback: () => unknown) => unknown;
    // The label to use for the restart button
    restartLabel: string;
    // A function that determines whether the current code should
    // cause the restart button to be hidden
    shouldHideRestart: () => boolean;
};

type RestartButtonState = {
    animating: boolean;
    hidden: boolean;
};

class RestartButton extends React.Component<
    RestartButtonProps,
    RestartButtonState
> {
    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQL
    _isMounted: boolean;

    constructor(props: RestartButtonProps) {
        super(props);

        this.state = {
            animating: false,
            hidden: true,
        };

        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
        this.props.registerCodeChange(this.handleUpdate);
        this.props.liveEditorPromise.then(this.handleUpdate);
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    handleUpdate = () => {
        if (!this._isMounted) {
            return;
        }
        this.setState({
            hidden: this.props.shouldHideRestart(),
        });
    };

    animate = () => {
        this.setState({animating: true});
        // TODO(somewhatabstract): Use WB timing
        // eslint-disable-next-line no-restricted-syntax
        window.setTimeout(() => {
            if (this._isMounted) {
                this.setState({animating: false});
            }
        }, 500);
    };

    render(): React.ReactNode {
        if (this.state.hidden) {
            return null;
        }
        return (
            // @ts-expect-error - TS2745 - Button expects children to be a string
            <Button
                onClick={this.animate}
                kind="secondary"
                id="restart-code"
                style={[styles.buttonOnRight, styles.restartButton]}
            >
                <span className={css(styles.restartLabel)}>
                    <PhosphorIcon
                        icon={arrowsCounterClockwiseIcon}
                        size="medium"
                        style={[
                            styles.restartIcon,
                            this.state.animating && styles.restartFlip,
                        ]}
                    />
                    <span>{this.props.restartLabel}</span>
                </span>
            </Button>
        );
    }
}

const ErrorBuddy = () => (
    <div className={css(styles.errorBuddyContainer, styles.buttonOnLeft)}>
        <div className="error-buddy-resting">
            <div
                className="error-buddy-happy"
                style={{display: "none", height: 15}}
            >
                <img alt="" src={staticUrl("/images/ohnoes-happy.png")} />
            </div>
            {/* live-editor make this clickable */}
            <button
                className={`error-buddy-thinking ${css(
                    styles.buttonReset,
                    styles.errorBuddyThinking,
                )}`}
                style={{display: "none"}}
            >
                <img alt="" src={staticUrl("/images/ohnoes-hmm.png")} />
                <span className={css(styles.errorBuddyHmm)}>
                    {}
                    {t({
                        context:
                            "The CS error buddy is thinking there might be an error in your code and is waiting for you to fix it.",
                        message: "Hmm...",
                    })}
                </span>
            </button>
        </div>
    </div>
);

const ChallengeTools = createReactClass({
    displayName: "ChallengeTools",

    propTypes: {
        // The challenge we are currently facing
        challenge: PropTypes.instanceOf(ScratchpadChallenge).isRequired,
    },

    mixins: [
        BackboneStateMixin({
            getModel: (props) => props.challenge,
            getState: (challenge) => ({
                appearanceReason:
                    !challenge.hasSyntaxErrors() &&
                    getCritterAppearanceReason(challenge),
                hasSyntaxErrors: challenge.hasSyntaxErrors(),
                currentStep: challenge.get("step"),
                stepStatus:
                    challenge.get("tests") &&
                    challenge.get("tests").map((test) => test.state === "pass"),
            }),
        }),
    ],

    shouldComponentUpdate(nextProps: unknown, nextState: unknown) {
        // @ts-expect-error - TS2571 - Object is of type 'unknown'.
        return !nextState.hasSyntaxErrors;
    },

    renderProgress() {
        const {currentStep, stepStatus} = this.state;
        return (
            stepStatus && (
                <ChallengeProgress
                    currentStep={currentStep}
                    onClickStep={(i) => this.props.challenge.moveToStep(i)}
                    stepStatus={stepStatus}
                />
            )
        );
    },

    renderCritter() {
        const challenge = this.props.challenge;
        if (!this.state.appearanceReason) {
            const nextStep = CritterAppearance.GO_TO_NEXT_STEP;
            const CritterButtonMessages = {
                [CritterAppearance.STEPS_COMPLETE]: t`Finish challenge`,
                [CritterAppearance.GO_TO_NEXT_STEP]: t`Next step`,
                [CritterAppearance.FIX_PREVIOUS_STEP]: t`Go back`,
            } as const;
            const label = CritterButtonMessages[nextStep];
            return (
                <Button disabled={true} style={styles.buttonOnRight}>
                    {label}
                </Button>
            );
        }
        const actions = {
            [CritterAppearance.STEPS_COMPLETE]: () =>
                challenge.completeChallenge(),
            [CritterAppearance.GO_TO_NEXT_STEP]: () =>
                challenge.moveToStep(challenge.firstFailure()),
            [CritterAppearance.FIX_PREVIOUS_STEP]: () =>
                challenge.moveToStep(challenge.firstFailure()),
        } as const;
        if (
            this.state.appearanceReason === CritterAppearance.CHALLENGE_COMPLETE
        ) {
            return (
                <CompletionCritter
                    challenge={challenge}
                    userScratchpad={challenge.get("userScratchpad")}
                    showAbove={false}
                />
            );
        }
        return (
            <ChallengeCritter
                reason={this.state.appearanceReason}
                onAcknowledge={actions[this.state.appearanceReason]}
            />
        );
    },

    render(): React.ReactNode {
        return (
            <div className={css(challengeStyles.challengeToolbar)}>
                <div className={css(challengeStyles.challengeProgressWrap)}>
                    {this.renderProgress()}
                </div>
                {this.renderCritter()}
            </div>
        );
    },
});

const challengeStyles = StyleSheet.create({
    challengeProgressWrap: {
        marginLeft: 16,
    },
    challengeToolbar: {
        display: "flex",
        marginLeft: 5,
    },
});

const styles = StyleSheet.create({
    shortModal: {
        maxHeight: 300,
    },
    buttonOnLeft: {
        marginRight: 16,
    },
    buttonOnRight: {
        marginLeft: 16,
    },
    errorBuddyContainer: {
        display: "inline-flex",
        height: 32,
        position: "relative",
        width: 50,

        cursor: "pointer",
    },
    errorBuddyThinking: {
        color: color.offBlack,
        ":hover": {
            textDecoration: "underline",
        },
    },
    errorBuddyHmm: {
        marginLeft: 8,
    },
    modalFooter: {
        flexDirection: "row",
    },
    restartButton: {
        paddingLeft: 12,
    },
    restartLabel: {
        alignItems: "center",
        display: "flex",
    },
    restartIcon: {
        display: "block",
        marginRight: 6,
    },
    restartFlip: {
        // @ts-expect-error - TS2322 - Type '{ "0%": { transform: string; }; "100%": { transform: string; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            "0%": {
                transform: "rotate(0deg)",
            },
            "100%": {
                transform: "rotate(-360deg)",
            },
        },
        animationDuration: "0.5s",
        animationIterationCount: "infinite",
    },
    // Adapted from https://gist.github.com/MoOx/9137295.
    buttonReset: {
        border: "none",
        margin: 0,
        padding: 0,
        overflow: "visible",
        outline: "none",
        cursor: "pointer",

        background: "transparent",

        /* inherit font & color from ancestor */
        color: "inherit",
        font: "inherit",

        /* Normalize `line-height`. Cannot be changed from `normal` in Firefox 4+. */
        lineHeight: "normal",

        /* Corrects font smoothing for webkit */
        WebkitFontSmoothing: "inherit",
        MozOsxFontSmoothing: "inherit",

        /* Corrects inability to style clickable `input` types in iOS */
        WebkitAppearance: "none",
    },
});

export default withBackboneModels((props: ScratchpadToolbarProps) => {
    const models = [props.scratchpad];

    if (props.userScratchpad) {
        models.push(props.userScratchpad);
    }

    if (props.challenge) {
        models.push(props.challenge);
    }

    return models;
})(ScratchpadToolbar) as React.ComponentType<
    JSX.LibraryManagedAttributes<
        typeof ScratchpadToolbar,
        React.ComponentProps<typeof ScratchpadToolbar>
    >
>;
